import React, { ReactNode, forwardRef } from "react";
import clsx from "clsx";
import { Loader2 } from "lucide-react";

// Base Switch Component
interface SwitchProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: string;
  helperText?: string;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "success" | "warning" | "error";
  icon?: ReactNode;
  labelPosition?: "left" | "right";
  className?: string;
  labelClassName?: string;
  id?: string;
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      checked = false,
      onChange,
      label,
      description,
      disabled = false,
      loading = false,
      error,
      helperText,
      size = "md",
      variant = "default",
      icon,
      labelPosition = "right",
      className,
      labelClassName,
      id,
      ...props
    },
    ref,
  ) => {
    const switchId = id || `switch-${Math.random().toString(36).substr(2, 9)}`;

    const sizeStyles = {
      sm: {
        track: "w-9 h-5",
        thumb: "h-4 w-4",
        translate: "translate-x-4",
      },
      md: {
        track: "w-11 h-6",
        thumb: "h-5 w-5",
        translate: "translate-x-5",
      },
      lg: {
        track: "w-14 h-7",
        thumb: "h-6 w-6",
        translate: "translate-x-7",
      },
    };

    const variantStyles = {
      default: {
        on: "bg-tracq-bright-blue",
        off: "bg-gray-700",
      },
      success: {
        on: "bg-green-600",
        off: "bg-gray-700",
      },
      warning: {
        on: "bg-yellow-600",
        off: "bg-gray-700",
      },
      error: {
        on: "bg-red-600",
        off: "bg-gray-700",
      },
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled && !loading) {
        onChange?.(e.target.checked);
      }
    };

    const currentSize = sizeStyles[size];
    const currentVariant = variantStyles[variant];

    const trackClasses = clsx(
      "relative inline-flex rounded-full transition-colors duration-200",
      currentSize.track,
      {
        [currentVariant.on]: checked && !disabled && !error,
        [currentVariant.off]: !checked && !disabled && !error,
        "bg-gray-800": disabled,
        "bg-red-600": error && !disabled,
      },
    );

    const thumbClasses = clsx(
      "absolute top-0.5 left-0.5 bg-white rounded-full transition-transform duration-200 flex items-center justify-center",
      currentSize.thumb,
      {
        [currentSize.translate]: checked,
      },
    );

    const labelContent = (
      <div className="flex flex-col">
        {label && (
          <span
            className={clsx("text-sm font-medium", disabled ? "text-gray-500" : "text-gray-200")}
          >
            {label}
          </span>
        )}
        {description && (
          <span className={clsx("text-xs mt-1", disabled ? "text-gray-500" : "text-gray-400")}>
            {description}
          </span>
        )}
      </div>
    );

    return (
      <div className={clsx("flex flex-col", className)}>
        <div
          className={clsx(
            "flex items-center gap-3",
            labelPosition === "left" ? "flex-row-reverse" : "flex-row",
          )}
        >
          {/* Hidden input */}
          <input
            ref={ref}
            type="checkbox"
            id={switchId}
            checked={checked}
            onChange={handleChange}
            disabled={disabled || loading}
            className="sr-only"
            {...props}
          />

          {/* Visual switch */}
          <label
            htmlFor={switchId}
            className={clsx(
              "cursor-pointer",
              (disabled || loading) && "cursor-not-allowed",
              labelClassName,
            )}
          >
            <div className={trackClasses}>
              <div className={thumbClasses}>
                {loading ? (
                  <Loader2 className="w-3 h-3 animate-spin text-gray-600" />
                ) : icon ? (
                  <div className="text-gray-600">{icon}</div>
                ) : null}
              </div>
            </div>
          </label>

          {/* Label and description */}
          {(label || description) && labelContent}
        </div>

        {/* Helper/Error text */}
        {(helperText || error) && (
          <div className={clsx("mt-2 text-sm", error ? "text-red-400" : "text-gray-400")}>
            {error || helperText}
          </div>
        )}
      </div>
    );
  },
);

Switch.displayName = "Switch";

// Switch Group Component
interface SwitchGroupProps {
  switches: Array<{
    id: string;
    checked?: boolean;
    onChange?: (checked: boolean) => void;
    label: string;
    description?: string;
    disabled?: boolean;
    loading?: boolean;
    error?: string;
    variant?: "default" | "success" | "warning" | "error";
    icon?: ReactNode;
  }>;
  label?: string;
  description?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
  orientation?: "vertical" | "horizontal";
}

export const SwitchGroup: React.FC<SwitchGroupProps> = ({
  switches,
  label,
  description,
  size = "md",
  className,
  orientation = "vertical",
}) => {
  return (
    <div className={clsx("space-y-4", className)}>
      {/* Group label */}
      {(label || description) && (
        <div>
          {label && <h3 className="text-sm font-medium text-gray-200">{label}</h3>}
          {description && <p className="text-xs text-gray-400 mt-1">{description}</p>}
        </div>
      )}

      {/* Switches */}
      <div
        className={clsx(
          "space-y-3",
          orientation === "horizontal" && "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
        )}
      >
        {switches.map((switchConfig) => (
          <Switch
            key={switchConfig.id}
            {...(switchConfig.checked !== undefined ? { checked: switchConfig.checked } : {})}
            {...(switchConfig.onChange !== undefined ? { onChange: switchConfig.onChange } : {})}
            label={switchConfig.label}
            {...(switchConfig.description !== undefined
              ? { description: switchConfig.description }
              : {})}
            {...(switchConfig.disabled !== undefined ? { disabled: switchConfig.disabled } : {})}
            {...(switchConfig.loading !== undefined ? { loading: switchConfig.loading } : {})}
            {...(switchConfig.error !== undefined ? { error: switchConfig.error } : {})}
            {...(switchConfig.variant !== undefined ? { variant: switchConfig.variant } : {})}
            icon={switchConfig.icon}
            size={size}
          />
        ))}
      </div>
    </div>
  );
};

// Icon Switch Component
interface IconSwitchProps extends Omit<SwitchProps, "icon"> {
  onIcon: ReactNode;
  offIcon?: ReactNode;
}

export const IconSwitch: React.FC<IconSwitchProps> = ({
  onIcon,
  offIcon,
  checked = false,
  ...props
}) => {
  return <Switch {...props} checked={checked} icon={checked ? onIcon : offIcon} />;
};

// Colored Switch Component
interface ColoredSwitchProps extends Omit<SwitchProps, "variant"> {
  color?: "blue" | "green" | "yellow" | "red" | "purple" | "pink";
}

export const ColoredSwitch: React.FC<ColoredSwitchProps> = ({ color = "blue", ...props }) => {
  const colorVariants = {
    blue: "default",
    green: "success",
    yellow: "warning",
    red: "error",
    purple: "default",
    pink: "default",
  } as const;

  return <Switch {...props} variant={colorVariants[color]} />;
};

// Toggle Switch Component (Alternative naming)
export const Toggle = Switch;
